import { icons } from "assets/icons/index";
import { BaseInput } from "components/inputs/Input/BaseInput";
import { Content, Horizontal } from "gls/lib";
import { isNumber } from "lodash";
import React, { forwardRef, useState } from "react";
import { colours, coloursNames } from "styling/colours";
import { style } from "typestyle";

export interface SearchInputProps {
  id?: string;
  width?: number | string;
  height?: number;
  value: string;
  onChange: (value: string) => void;
  onEnterSearch?: (value: string) => Promise<any>;
  placeHolder?: string;
  onBlur?: () => void;
  onClear?: () => void;
  onFocus?: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>((props, inputRef) => {
  const { value, onChange, onEnterSearch } = props;
  const compWidth = isNumber(props.width) ? `${props.width || 290}px` : props.width;
  const compWidthImportant = `${compWidth} !important`;
  const height = props.height || 40;
  const [latestSearchTerm, setLatestSearchTerm] = useState('')
  const [searchError, setSearchError] = useState('')

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter' && onEnterSearch) {
      setLatestSearchTerm(value)
      onEnterSearch(value).then((error) => {
        if (error)
          setSearchError(error)
      })
    }
  }

  const handleClear = () => {
    onChange("");
    setSearchError('')
    if (latestSearchTerm !== "")
      onEnterSearch && onEnterSearch("")
  };

  return (
    <Content
      className={style({ width: compWidth, position: "relative" })}
    >
      <icons.SearchIcon
        aria-label="Search"
        fill={colours.secondaryText}
        className={style({
          position: "absolute",
          top: height / 2 - 8,
          left: 16,
        })}
      />
      <BaseInput
        ref={inputRef}
        id={props.id || "base-search-input"}
        className={baseStyle(compWidthImportant, height)}
        type="search"
        title="Search"
        placeholder={props.placeHolder}
        value={value}
        onChange={(value) => {
          onChange(value)
          setSearchError('')
        }}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onKeyDown={handleKeyPress}
      />
      {value?.length ? (
        <Content onClick={handleClear} className={contentStyle(height)}>
          <icons.clearFieldIcon aria-label="Clear" width={16} height={16} className={iconStyle} />
        </Content>
      ) : null}
      {searchError && (
        <Horizontal spacing={5} className={errorStyle}>
          <icons.ErrorIcon aria-label="Error" />
          <span>{searchError}</span>
        </Horizontal>
      )}
    </Content>
  );
});

const errorStyle = style({
  position: "absolute",
  bottom: "-40px",
  background: "white",
  width: "100%",
  height: "40px",
  padding: "9px",
  zIndex: "9999",
  border: "1px solid rgba(188, 190, 192, 0.3)",
  borderRadius: "3px",
  color: coloursNames.cinnabarOrange
})

const baseStyle = (compWidthImportant: string, height: number) =>
  style({
    width: compWidthImportant,
    height: `${height}px !important`,
    borderRadius: "6px !important",
    borderColor: colours.secondaryText,
    $nest: {
      'input': {
        padding: "0 40px !important",
        backgroundColor: colours.white,
        $nest: {
          "&:focus": {
            background: "white",
            borderColor: `${colours.secondary} !important`,
          },
        },
      },
      "::placeholder": {
        color: colours.secondaryText,
      },
    },
  });

const contentStyle = (height: number) => style({
  cursor: "pointer",
  position: "absolute",
  height: "16px",
  width: "16px",
  borderRadius: "16px",
  top: height / 2 - 8,
  right: 12,
});

const iconStyle = style({
  position: "absolute",
  top: 0,
  left: 0,
  $nest: {
    "path": {
      fill: `${colours.tertiaryText} !important`,
    },
  },
});
